import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { alpha, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import DynamicCrudPage from '../../components/DynamicCrudPage';
import { JobDetailsModal } from '../../components';
import useJobAssignments from '../../hooks/useJobAssignments';
import { useTeams, useUsers, useGroups, useFirms, SCREENS, useNotification } from '../../hooks';
import { hasuraClient } from '../../utils/graphqlClient';
import { GET_PENDING_FINANCIAL_REQUESTS } from '../../graphql/queries/reportFinancialRequests';
import { UPDATE_JOB_NO_FINANCIAL_NEEDED } from '../../graphql/mutations';
import { useAuth } from '../../hooks';
import JobAssignmentModal from './JobAssignmentModal';

const JobAssignmentsPage = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const jobAssignmentsData = useJobAssignments();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedJobForStatus, setSelectedJobForStatus] = useState(null);
  const [pendingFinancialRequests, setPendingFinancialRequests] = useState([]);

  const fetchPendingFinancialRequests = useCallback(async () => {
    if (!user?.companyId) return;
    try {
      const result = await hasuraClient.query(GET_PENDING_FINANCIAL_REQUESTS, {
        where: { company_id: { _eq: user.companyId } },
      });
      setPendingFinancialRequests(result?.report_financial_requests || []);
    } catch (err) {
      console.error('Pending financial requests fetch error:', err);
      setPendingFinancialRequests([]);
    }
  }, [user?.companyId]);

  useEffect(() => {
    fetchPendingFinancialRequests();
  }, [fetchPendingFinancialRequests, jobAssignmentsData.refreshTrigger]);

  const pendingJobIdSet = useMemo(() => {
    const set = new Set();
    for (const req of pendingFinancialRequests) {
      if (req?.job_id) set.add(req.job_id);
    }
    return set;
  }, [pendingFinancialRequests]);

  // Hook çıktısını sar: pending mali talep olan satırlara warning mesajı +
  // warningType ekle. Tabloda zaten 'warningMessage' kolonu bu alanı render
  // ettiği için ekstra kolon gerekmiyor.
  const augmentedJobAssignmentsData = useMemo(() => {
    const requestedLabel = t(
      'jobAssignments.financialRequests.requestPendingChip',
      { defaultValue: 'Mali sorumlu talep edildi' }
    );
    const rows = jobAssignmentsData.jobAssignments || [];
    if (pendingJobIdSet.size === 0) return jobAssignmentsData;

    const next = rows.map((row) => {
      const jobId = row?.job_id || row?.id;
      if (!jobId || !pendingJobIdSet.has(jobId)) return row;
      return {
        ...row,
        warningMessage: requestedLabel,
        warningType: 'NO_FINANCIAL',
      };
    });

    return { ...jobAssignmentsData, jobAssignments: next };
  }, [jobAssignmentsData, pendingJobIdSet, t]);

  const handleOpenAssignModal = useCallback((rowData) => {
    const isUnassigned = !!rowData.is_unassigned;
    const actualJobId = isUnassigned ? rowData.id : rowData.job_id;
    const actualAssignmentId = isUnassigned ? (rowData.assignment_id || null) : rowData.id;
    const jobDetails = rowData.job || {};

    setSelectedJob({
      id: actualJobId,
      job_id: actualJobId,
      job_title: jobDetails.job_title || jobDetails.jobTitle || rowData.jobTitle,
      job_status: jobDetails.job_status || jobDetails.jobStatus || rowData.jobStatus,
      consultingScope: jobDetails.consulting_scope || jobDetails.consultingScope || rowData.consultingScope || 'MALI_TEKNIK',
      assignment_id: actualAssignmentId,
      assignmentData: rowData,
    });

    setIsModalOpen(true);
  }, []);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedJob(null);
  };

  const handleSuccess = () => {
    jobAssignmentsData.triggerRefresh?.();
    fetchPendingFinancialRequests();
  };

  const handleOpenStatusModal = useCallback((rowData) => {
    const job = rowData?.job;
    if (!job) return;
    setSelectedJobForStatus(job);
    setStatusModalOpen(true);
  }, []);

  const handleCloseStatusModal = useCallback(() => {
    setStatusModalOpen(false);
    setSelectedJobForStatus(null);
  }, []);

  const customRowActions = useCallback(() => {
    return [
      {
        type: 'assign',
        label: 'Sorumlu Ata / Güncelle',
        icon: <AssignmentIndIcon fontSize="small" />,
        color: 'primary',
      },
      {
        type: 'viewJob',
        label: 'jobs.viewStatus',
        icon: <VisibilityIcon fontSize="small" />,
        color: 'info',
        showIf: (r) => !!r.job,
      },
      // "Mali sorumlu istenmiyor" işaretle: iş bu listeden düşer, dolayısıyla
      // geri alma (tekrar iste) aksiyonu burada DEĞİL — İşler sayfasında.
      {
        type: 'noFinancialOff',
        label: 'jobAssignments.actions.noFinancialOff',
        icon: <BlockIcon fontSize="small" />,
        color: 'warning',
        showIf: (r) =>
          (r?.job?.consulting_scope || r?.consultingScope) !== 'SADECE_TEKNIK' &&
          !r?.job?.no_financial_needed,
      },
    ];
  }, []);

  const { showNotification } = useNotification();

  const toggleNoFinancialNeeded = useCallback(async (row, value) => {
    const jobId = row?.job?.id || row?.job_id || row?.id;
    if (!jobId) return;
    try {
      await hasuraClient.mutate(UPDATE_JOB_NO_FINANCIAL_NEEDED, {
        id: jobId,
        value,
      });
      jobAssignmentsData.triggerRefresh?.();
      fetchPendingFinancialRequests();
      showNotification(
        value
          ? t('jobAssignments.actions.noFinancialOffSuccess', 'Mali sorumlu istenmiyor olarak işaretlendi.')
          : t('jobAssignments.actions.noFinancialOnSuccess', 'Mali sorumlu talebi tekrar aktif.'),
        'success'
      );
    } catch (err) {
      showNotification(
        t('jobAssignments.actions.noFinancialError', 'İşlem sırasında hata oluştu.'),
        'error'
      );
    }
  }, [jobAssignmentsData, fetchPendingFinancialRequests, showNotification, t]);

  const handleRowAction = useCallback((actionType, row) => {
    if (actionType === 'assign' && row) {
      handleOpenAssignModal(row);
      return true;
    }
    if (actionType === 'viewJob' && row) {
      handleOpenStatusModal(row);
      return true;
    }
    if (actionType === 'noFinancialOff' && row) {
      toggleNoFinancialNeeded(row, true);
      return true;
    }
    return false;
  }, [handleOpenAssignModal, handleOpenStatusModal, toggleNoFinancialNeeded]);

  // Bekleyen mali sorumlu talebi olan satırlar — belirgin görünmesi için
  // kalın sol border + daha doygun arka plan, hücreler için outline.
  const getRowSx = useCallback((row) => {
    const jobId = row?.job_id || row?.id;
    if (!jobId || !pendingJobIdSet.has(jobId)) return {};
    const color = theme.palette.warning.main;
    return {
      bgcolor: alpha(color, 0.18),
      '&:nth-of-type(odd)': { bgcolor: alpha(color, 0.18) },
      '&:hover': { bgcolor: alpha(color, 0.28) },
      '&:nth-of-type(odd):hover': { bgcolor: alpha(color, 0.28) },
      borderLeft: `6px solid ${color}`,
      '& > td:first-of-type': { fontWeight: 700 },
    };
  }, [pendingJobIdSet, theme]);

  return (
    <>
      <DynamicCrudPage
        entityType="jobAssignments"
        screen={SCREENS.JOB_ASSIGNMENTS}
        hook={() => augmentedJobAssignmentsData}
        additionalHooks={{
          firms: useFirms,
          teams: useTeams,
          users: useUsers,
          groups: useGroups,
        }}
        showAddButton={false}
        hideDefaultActions={true}
        customRowActions={customRowActions}
        onRowAction={handleRowAction}
        getRowSx={getRowSx}
        addButtonIcon={<AssignmentIndIcon />}
        addButtonTextKey="table.actions.addJobAssignment"
      />

      <JobAssignmentModal
        open={isModalOpen}
        onClose={handleCloseModal}
        selectedJob={selectedJob}
        onSuccess={handleSuccess}
      />

      <JobDetailsModal
        open={statusModalOpen}
        onClose={handleCloseStatusModal}
        job={selectedJobForStatus}
      />
    </>
  );
};

export default JobAssignmentsPage;
